<template>
    <dialog-window v-model="isOpen" data-id="editPhoneDialog" :title="title" @close="closeDialogHandler">
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section :is-loading="isLoading">
                <phone-field
                    id="customer-phone"
                    v-model="formData.phone"
                    data-id="editPhoneDialogPhoneField"
                    :label="t('profile.newPhone')"
                />
                <phone-field
                    id="customer-confirm-phone"
                    v-model="formData.phoneConfirm"
                    data-id="editPhoneDialogConfirmPhoneField"
                    :reference-value="formData.phone"
                    :reference-error-label="t('profile.newPhonesMustMatch')"
                    :label="t('profile.confirmNewPhone')"
                />
                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        data-id="editPhoneDialogCancelButton"
                        class="flex-1-0-0"
                        size="large"
                        :inactive-label="t('form.cancel')"
                        :disabled="isSubmitting"
                        @click="closeDialogHandler"
                    >
                        {{ t('profile.cancel') }}
                    </card-button>
                    <card-button
                        data-id="editPhoneDialogSubmitButton"
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        variant="flat"
                        :loading="isLoading"
                        :inactive-label="t('inquiry.sendMessage')"
                        :disabled="isSubmitting"
                    >
                        {{ t('profile.profileSave') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
    </dialog-window>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { useContainer } from '@/plugins/inversify';
import { useFormData, useFormHandling, useNotification } from '@/features/common/components/form/composables';
import { FormSection, NotificationBar, PhoneField } from '@/features/common/components/form';
import { DEFAULT_COUNTRY } from '@/features/common/components/form/constants';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import { useGlobalDialogStore } from '@/store/dialogStore';
import { IEditProfileDialog } from '../types';
import { PhoneDto, UpdateProfileDto } from '@/types/webapi';
import CardButton from '@/features/common/components/CardButton.vue';
import DialogWindow from '@/features/common/components/DialogWindow.vue';

const { t } = useI18n();
const userStore = useUserStore();
const container = useContainer();
const { isDialogOpen, closeDialog } = useGlobalDialogStore();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const props = defineProps<IEditProfileDialog>();

const isOpen = computed(() => isDialogOpen(props.type));

const isLoading = ref(false);

const defaultPhone: PhoneDto = { countryCode: DEFAULT_COUNTRY.PHONE_CODE, number: '' };

interface IUpdatePhoneData {
    phone: PhoneDto;
    phoneConfirm: PhoneDto;
}

const updatePhoneData: IUpdatePhoneData = {
    phone: defaultPhone,
    phoneConfirm: defaultPhone
};

const { form, isSubmitting, validateForm, toggleIsSubmitting, isFormEmpty } = useFormHandling();
const { notificationBarState, notificationError, notificationSuccess } = useNotification();
const { formData, resetFormData } = useFormData(updatePhoneData);

const saveData = async (data: IUpdatePhoneData) => {
    try {
        const updateProfileDto = {
            firstName: userStore.profile.firstName,
            lastName: userStore.profile.lastName,
            phone: {
                countryCode: data.phone.countryCode,
                number: data.phone.number
            } as PhoneDto
        } as UpdateProfileDto;

        await profileApi.updateProfile(updateProfileDto);
        await userStore.loadUser();
        notificationBarState.value = notificationSuccess(t('profile.phoneSavedSuccess'));
        return true;
    } catch {
        notificationBarState.value = notificationError(t('profile.phoneSavedError'));
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        isLoading.value = true;
        const result = await saveData(formData.value);
        result && closeDialog(props.type);
    } finally {
        toggleIsSubmitting(false);
        isLoading.value = false;
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

const closeDialogHandler = () => {
    resetFormData(form);
    closeDialog(props.type);
};
</script>
